import React, { useEffect, useState } from "react";
import { FaGithub, FaLinkedin, FaXTwitter } from "react-icons/fa6";
import { HiOutlineDownload } from "react-icons/hi";
import { motion } from "framer-motion";
import Typewriter from "typewriter-effect";
import { Link } from "react-router-dom";

const StarBackground = () => {
  const [stars, setStars] = useState([]);
  const [meteors, setMeteors] = useState([]);
  const [parallaxX, setParallaxX] = useState(0);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    generateStars();
    generateMeteors();

    const handleResize = () => generateStars();
    const handleMouseMove = (e) => {
      setParallaxX((e.clientX / window.innerWidth - 0.5) * 10);
      setParallaxY((e.clientY / window.innerHeight - 0.5) * 10);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const generateStars = () => {
    const count = Math.floor((window.innerWidth * window.innerHeight) / 8000);
    setStars(
      Array.from({ length: count }, (_, i) => ({
        id: i,
        size: Math.random() * 2 + 1,
        x: Math.random() * 100,
        y: Math.random() * 100,
        opacity: Math.random() * 0.6 + 0.3,
        speed: Math.random() * 2 + 1,
      }))
    );
  };

  const generateMeteors = () => {
    const newMeteors = Array.from({ length: 4 }, (_, i) => ({
      id: i,
      size: Math.random() * 1.5 + 1,
      x: Math.random() * 100,
      y: Math.random() * 20,
      delay: Math.random() * 10,
      animationDuration: Math.random() * 3 + 3,
    }));
    setMeteors(newMeteors);
  };

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            left: `${star.x}%`,
            top: `${star.y}%`,
            opacity: star.opacity,
            position: "absolute",
            borderRadius: "9999px",
            backgroundColor: "white",
            boxShadow: "0 0 15px 3px rgba(255,255,255,0.5)",
            animation: `twinkle ${star.speed}s ease-in-out infinite`,
            transform: `translate(${
              parallaxX * (star.speed / 2)
            }px, ${parallaxY * (star.speed / 2)}px)`,
          }}
        />
      ))}
      {meteors.map((meteor) => (
        <div
          key={meteor.id}
          style={{
            width: `${meteor.size * 50}px`,
            height: `${meteor.size * 2}px`,
            left: `${meteor.x}%`,
            top: `${meteor.y}%`,
            animationDelay: `${meteor.delay}s`,
            animationDuration: `${meteor.animationDuration}s`,
            position: "absolute",
            borderRadius: "9999px",
            background: "linear-gradient(to right, white, transparent)",
            boxShadow: "0 0 20px 5px rgba(255, 255, 255, 0.3)",
            transform: "rotate(-45deg)",
            animationName: "meteor",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @keyframes meteor {
          0% { transform: translate(0, 0) rotate(-45deg); opacity: 1; }
          100% { transform: translate(300px, 300px) rotate(-45deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const stats = [
  { label: "Years of Learning", value: "1+" },
  { label: "Passion Level", value: "∞" },
];

const Home = () => {
  const [showAbout, setShowAbout] = useState(false);

  return (
    <section className="relative text-white px-4 sm:px-6 py-16 sm:py-24 min-h-screen flex items-center justify-center overflow-hidden">
      <StarBackground />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-gray-800 opacity-80 z-0" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12"
      >
        {/* Profile Image */}
        <motion.div
          whileHover={{ scale: 1.05 }}
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
          className="relative w-52 h-52 sm:w-64 sm:h-64 flex-shrink-0 rounded-full overflow-hidden border-4 border-emerald-500 shadow-lg"
        >
          <img
            src="/harsh1.JPG"
            alt="Harsh Pariya"
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Info */}
        <div className="text-center md:text-left space-y-6 max-w-xl">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight bg-gradient-to-r from-emerald-400 via-sky-400 to-indigo-500 text-transparent bg-clip-text leading-snug">
            <Typewriter
              options={{
                strings: [
                  "Hi, I'm Harsh Pariya",
                  "Aspiring Full-Stack Developer!",
                  "Code. Design. Build.",
                ],
                autoStart: true,
                loop: true,
              }}
            />
          </h1>

          <p className="text-lg text-slate-300 leading-relaxed">
            I build modern, responsive, and scalable full-stack web applications. Passionate about turning complex problems into elegant solutions.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start pt-2">
            <Link
              to="/projects"
              className="bg-gradient-to-r from-sky-500 via-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg shadow-md hover:scale-105 transition"
            >
              View Projects
            </Link>
            <a
              href="/Resume.pdf"
              download
              className="flex items-center justify-center gap-2 border border-emerald-400 text-emerald-300 hover:bg-emerald-600 hover:text-white px-6 py-3 rounded-lg hover:scale-105 transition"
            >
              <HiOutlineDownload className="text-xl" /> Resume
            </a>
          </div>

          <div className="flex gap-6 justify-center md:justify-start mt-6 text-2xl text-slate-300">
            <a
              href="https://github.com/HarshPariya"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-emerald-400 transition hover:drop-shadow-glow"
            >
              <FaGithub />
            </a>
            <a
              href="https://www.linkedin.com/in/harsh-pariya/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-sky-400 transition hover:drop-shadow-glow"
            >
              <FaLinkedin />
            </a>
            <a
              href="https://x.com/harshpariya_01"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-indigo-400 transition hover:drop-shadow-glow"
            >
              <FaXTwitter />
            </a>
          </div>

          <button
            onClick={() => setShowAbout(!showAbout)}
            className="mt-4 text-sm underline text-slate-400 hover:text-emerald-300 transition"
          >
            {showAbout ? "Hide About Me" : "Read More About Me"}
          </button>

          {showAbout && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-slate-400"
            >
              I'm currently learning Full-Stack Developing, working on real-time apps, and exploring new ways to deliver smooth, animated UIs using Next.JS + React + Tailwind + Framer Motion.
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.6 }}
        className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 w-full max-w-5xl px-4 z-10"
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center text-slate-300">
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              whileHover={{ scale: 1.05 }}
              className="transition-transform duration-300"
            >
              <h4 className="text-2xl sm:text-3xl font-extrabold text-transparent bg-gradient-to-r from-emerald-400 via-sky-400 to-indigo-500 bg-clip-text">
                {stat.value}
              </h4>
              <p className="text-sm mt-1 text-slate-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Home;
